import React from 'react';
import { FINANCIAL_PILLS, FULFILLMENT_PILLS, humanizeStatus } from './statusPillMaps';

/**
 * Status pills for a draft order / quote.
 *
 * When a linked order exists, financial + fulfillment pills match OrderStatusPills.
 * Otherwise shows quote workflow status (open, pending, expired, customer actions).
 */

const DRAFT_STATUS_PILLS = {
  open_quote: { label: 'open quote', className: 'pill-button-open' },
  pending: { label: 'pending', className: 'pill-button-invoice-sent' },
  completed: { label: 'completed', className: 'pill-button-completed' },
};

function lookupPill(map, status) {
  if (!status) return null;
  return map[status] || { label: humanizeStatus(status), className: 'pill-button-grey' };
}

function DraftOrderStatusPills({
  draftOrder,
  expired,
  expirationColorClass,
  expirationText,
  className = '',
}) {
  if (!draftOrder) return null;

  const order = draftOrder.order;
  const status = (draftOrder.status || '').toLowerCase();
  const isCompleted = status === 'completed';

  if (order?.cancelledAt) {
    return (
      <div className={`draft-order-status-container ${className}`}>
        <span className="pill-button draft-order-status pill-button-expired">Cancelled</span>
      </div>
    );
  }

  const pills = [];

  if (order) {
    
    const financial = (order.financialStatus || '').toLowerCase();
    const fulfillment = (order.fulfillmentStatus || '').toLowerCase();
    const isGovPO = !!order.governmentPo;
    const hasPOFile = !!order.governmentPoDocument;
    const poSent = order.poSent;
    const isFulfilled = fulfillment === 'fulfilled';
    const isPartiallyFulfilled = fulfillment === 'partially_fulfilled';
    const hideFulfillment = financial === 'pending' && fulfillment === 'unfulfilled';

    if (isGovPO && !hasPOFile) {
      pills.push({ label: 'Awaiting Purchase Order', className: 'pill-button-red' });
    } else if (isGovPO && !poSent && hasPOFile && fulfillment === 'unfulfilled') {
      pills.push({ label: 'PO Under Review', className: 'pill-button-grey' });
    } else if (isGovPO && poSent && hasPOFile && fulfillment === 'unfulfilled') {
      pills.push({ label: 'PO Accepted', className: 'pill-button-grey' });
      pills.push({ label: 'Processing Order', className: 'pill-button-yellow' });
    } else if (isGovPO && isFulfilled && financial === 'pending') {
      pills.push({ label: 'Payment Due', className: 'pill-button-red' });
    } else if (isGovPO && isPartiallyFulfilled && financial === 'pending') {
      pills.push({ label: 'Payment Due', className: 'pill-button-orange' });
    } else {
      const financialPill = lookupPill(FINANCIAL_PILLS, financial);
      if (financialPill) pills.push(financialPill);
    }

    if (!hideFulfillment) {
      const fulfillmentPill = lookupPill(FULFILLMENT_PILLS, fulfillment);
      if (fulfillmentPill) pills.push(fulfillmentPill);
    }
  } else if (expired && !isCompleted) {
    pills.push({ label: 'Expired Quote', className: 'pill-button-expired' });
  } else if (!expired && !isCompleted && (status === 'pending' || status === 'open_quote')) {
    const draftStatusPill = DRAFT_STATUS_PILLS[status] || {
      label: humanizeStatus(status),
      className: 'pill-button-expired',
    };
    pills.push(draftStatusPill);

    if (draftOrder.approved) {
      pills.push({ label: 'Approved', className: 'pill-button-green' });
    } else if (draftOrder.declined) {
      pills.push({ label: 'Declined', className: 'pill-button-red' });
    } else if (draftOrder.needsUpdates) {
      pills.push({ label: 'Updates Requested', className: 'pill-button-purple' });
    } else if (draftOrder.needsExtension) {
      pills.push({ label: 'Extension Requested', className: 'pill-button-yellow' });
    }
  } else if (isCompleted) {
    pills.push(DRAFT_STATUS_PILLS.completed);
  }

  if (draftOrder.expirationDate && !expired && !isCompleted) {
    pills.push({ label: expirationText, className: expirationColorClass });
  }

  if (draftOrder.taxExempt) {
    pills.push({ label: 'Tax Exempt', className: 'pill-button-tax-exempt' });
  }

  if (pills.length === 0) return null;

  return (
    <div className={`draft-order-status-container ${className}`}>
      {pills.map((pill, i) => (
        <span
          key={`${pill.label}-${i}`}
          className={`pill-button draft-order-status ${pill.className || ''}`}
        >
          {pill.label}
        </span>
      ))}
    </div>
  );
}

export default DraftOrderStatusPills;
